import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import DatePicker, { registerLocale } from 'react-datepicker'
import es from 'date-fns/locale/es'
import 'react-datepicker/dist/react-datepicker.css'
import { ChevronLeft, UserAvatar } from '../../../../assets/icons'
import { BackTitle } from '../../EmergencyRoom/styled'
import { capitalizeEachWord } from '../../../../utils/stringsUtils'
import useIsMobile from '../../../../hooks/useIsMobile'
import { Button } from '../../../../components/ui/button'

registerLocale('es', es)

const formatHour = date => {
	if (!date) return ''
	return new Date(date).toLocaleTimeString('es-AR', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false
	})
}

const normalizeDate = date => {
	const d = new Date(date)
	return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

const getNearestTurno = (turnos = []) => {
	const sorted = [...turnos].sort(
		(a, b) => new Date(a.fechaTurnoDateTime) - new Date(b.fechaTurnoDateTime)
	)
	return sorted[0] || null
}

const SelectAppointment = ({ turnosDisponibles = [], onBack, onConfirm }) => {
	const isMobile = useIsMobile()
	const nearest = useMemo(() => getNearestTurno(turnosDisponibles), [turnosDisponibles])
	const [selectedDate, setSelectedDate] = useState(
		nearest ? new Date(nearest.fechaTurnoDateTime) : null
	)
	const [selectedTurnoId, setSelectedTurnoId] = useState(nearest?.idTurno || null)

	useEffect(() => {
		if (nearest) {
			setSelectedDate(new Date(nearest.fechaTurnoDateTime))
			setSelectedTurnoId(nearest.idTurno)
		}
	}, [nearest])

	const availableDates = useMemo(() => {
		const uniq = new Map()
		turnosDisponibles.forEach(t => {
			const day = normalizeDate(t.fechaTurnoDateTime).getTime()
			uniq.set(day, new Date(day))
		})
		return Array.from(uniq.values()).sort((a, b) => a - b)
	}, [turnosDisponibles])

	const turnosByDate = useMemo(() => {
		if (!selectedDate) return []
		const dayKey = normalizeDate(selectedDate).getTime()
		return turnosDisponibles
			.filter(t => normalizeDate(t.fechaTurnoDateTime).getTime() === dayKey)
			.sort((a, b) => new Date(a.fechaTurnoDateTime) - new Date(b.fechaTurnoDateTime))
	}, [selectedDate, turnosDisponibles])

	const handleSelectDate = date => {
		setSelectedDate(date)
		setSelectedTurnoId(null)
	}

	const handleSelectHour = turnoId => {
		setSelectedTurnoId(turnoId)
	}

	const handleContinue = () => {
		if (!selectedTurnoId) return
		const selected = turnosDisponibles.find(t => t.idTurno === selectedTurnoId)
		onConfirm(selected)
	}

  return (
    <Container>
      <Header>
        {!isMobile && (
          <BackTitle m={'0'} onClick={onBack}>
            <ChevronLeft size={8} /> Volver
          </BackTitle>)
        }
        <Title>Agendar turno</Title>
      </Header>

      <DoctorCard>
        <Avatar><UserAvatar color='#161616' /></Avatar>
        <div>
          <DoctorName>{capitalizeEachWord(nearest?.nombreProfesional)}</DoctorName>
          <DoctorMeta>{capitalizeEachWord(nearest?.nombreEspecialidad)} - {capitalizeEachWord(nearest?.clinica)}</DoctorMeta>
        </div>
      </DoctorCard>

			<Grid>
				<CalendarBlock>
					<SectionTitle>Fecha</SectionTitle>
					<DatePicker
						selected={selectedDate}
						onChange={handleSelectDate}
						includeDates={availableDates}
						disabledKeyboardNavigation
						locale="es"
						dateFormat="dd/MM/yyyy"
						inline
					/>
				</CalendarBlock>

				<HoursBlock>
					<SectionTitle>Hora</SectionTitle>
					<HoursGrid>
						{turnosByDate.map(t => (
							<HourButton
								key={t.idTurno}
								type="button"
								onClick={() => handleSelectHour(t.idTurno)}
								$active={t.idTurno === selectedTurnoId}
							>
								{formatHour(t.fechaTurnoDateTime)} hs
							</HourButton>
						))}
						{turnosByDate.length === 0 && <EmptyText>No hay horarios para esta fecha</EmptyText>}
					</HoursGrid>
				</HoursBlock>
			</Grid>

			<Actions>
				<Button
					type="button"
					$variant="primary"
					disabled={!selectedTurnoId}
					onClick={handleContinue}
					label={'Continuar'}
					size={isMobile ? 'mobile' : 'xlarge'}
				/>
			</Actions>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	height: 80vh;
	width: 80vw;
	padding: 8px 12px 16px 12px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		height: 100%;
		width: 100%;
	}
`

const Header = styled.div`
	display: flex;
	align-items: flex-start;
	flex-direction: column;
	gap: 6px;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 24px 0;
`

const DoctorCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fff;
  max-width: 27%;
  min-width: 325px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		max-width: 100%;
	}
`

const Avatar = styled.div`
	width: 32px;
	height: 32px;
	display: grid;
	place-items: center;
	border: 1px solid #d0d0d0;
	border-radius: 50%;
	font-size: 16px;
`

const DoctorName = styled.div`
  font-weight: 600;
  font-size: 16px;
  color: #222;
`

const DoctorMeta = styled.div`
  font-size: 14px;
  color: #4D4D4D;
`

const Grid = styled.div`
	display: flex;
	gap: 20px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		display: grid;
		grid-template-columns: 1fr;
		gap: 20px;
		align-items: flex-start;
	}
`

export const CalendarBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 27%;
	min-width: 325px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: 100%;
		min-width: 0;
	}

  /* Estilos para el DatePicker */
  .react-datepicker {
    display: block;
    width: 100%;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    font-family: inherit;
  }

	.react-datepicker__month-container {
		float: none;
		width: 100%;
	}

	.react-datepicker-wrapper {
		width: 100%;
	}

	.react-datepicker__header {
		background-color: white;
		border-bottom: none;
		border-radius: 12px 12px 0 0;
		padding: 12px;
	}

  .react-datepicker__current-month {
    font-size: 18px;
    padding-left: 24px;
    font-weight: 600;
    color: #161616;
    width: 100%;
    text-align: left;
    text-transform: capitalize;
  }

	.react-datepicker__navigation-icon {
		width: 6px;
		height: 6px;
	}

  .react-datepicker__navigation--previous {
    left: auto;
    right: 42px;
  }

  .react-datepicker__navigation-next {
    overflow: visible;
    width: 10px;
    right: 14px;
  }

	.react-datepicker__day-names {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		gap: 2px;
		padding: 8px 12px 4px 12px;
		margin: 0;
		background: #fff;
	}

	.react-datepicker__day-name {
		font-size: 12px;
		font-weight: 600;
		color: #888;
		text-align: center;
		padding: 4px 0;
	}

	/* Ensure weekday names and day cells occupy identical columns */
	.react-datepicker__day-name,
	.react-datepicker__day {
		width: 100%;
		margin: 0;
		box-sizing: border-box;
	}

	.react-datepicker__month {
		margin: 0;
		display: grid;
		gap: 2px;
		padding: 0 12px 12px 12px;
	}

	.react-datepicker__week {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		gap: 2px;
		margin: 0;
	}

	.react-datepicker__day {
		aspect-ratio: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 13px;
		color: #333;
		border: none;
		border-radius: 6px;
		background: #fff;
		cursor: pointer;
		transition: all 0.2s ease;
		padding: 0;

		&:hover:not(.react-datepicker__day--outside-month) {
			background: #f0f0f0;
		}
	}

	.react-datepicker__day--selected {
		background-color: #003da5;
		color: #fff;
		font-weight: 600;
		border-radius: 50%;

		&:hover {
			background-color: #003da5;
		}
	}

	.react-datepicker__day--keyboard-selected {
		background-color: transparent;
		color: #333;
		border-radius: 6px;
	}

	.react-datepicker__day--keyboard-selected.react-datepicker__day--selected {
		background-color: #003da5;
		color: #fff;
		border-radius: 50%;
	}

	.react-datepicker__day--outside-month {
		color: #ccc;
		cursor: not-allowed;
	}

	.react-datepicker__day--disabled {
		color: #ccc;
		cursor: not-allowed;
	}
`

const HoursBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 30%;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		width: auto;
	}
`

const SectionTitle = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #222;
`

const HoursGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
	gap: 8px;
`

const HourButton = styled.button`
	padding: 10px 12px;
	border-radius: 8px;
	border: 1px solid ${p => (p.$active ? p.theme.colors.brand.blueOmint : '#d9d9d9')};
	background: ${p => (p.$active ? p.theme.colors.brand.blueOmint : '#fff')};
	color: ${p => (p.$active ? '#fff' : '#333')};
	font-size: 14px;
	cursor: pointer;
	transition: all 0.15s ease;

	&:hover {
		border-color: ${p => (p.$active ? p.theme.colors.brand.blueOmint : '#999')};
	}
`

const EmptyText = styled.div`
	grid-column: 1 / -1;
	font-size: 14px;
	color: #777;
`

const Actions = styled.div`
	display: flex;
	justify-content: flex-start;
	margin-top: 8px;
	max-width: 320px;

	@media (max-width: ${props => props.theme.sizes.breakPoints.small}) {
		max-width: 100%;
		width: 100%;
	}
`

export default SelectAppointment
